import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { KYCFlowController } from '../flows/KYCFlowController';
import type { KYCResult, KYCSession } from '../types/kyc';

// Modo producción: se ejecuta dentro de un iframe/modal abierto por Amarello.com.
// Lee session_id y token de los parámetros de URL.
export function EmbeddedMode() {
  const [session, setSession] = useState<KYCSession | null>(null);
  const [paramsError, setParamsError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const token = params.get('token');

    if (!sessionId || !token) {
      setParamsError(true);
      return;
    }

    // liveness_enabled es opcional — si el gateway no lo envía, el flujo lo desactiva por defecto
    const livenessEnabled = params.get('liveness_enabled') === 'true';
    // D-7: user_id opcional — si el gateway lo provee, el flujo inicia en CHECKING_STATUS
    // para verificar el estado KYC previo del usuario antes de permitir una nueva verificación.
    const userId = params.get('user_id') ?? undefined;
    setSession({ sessionId, token, livenessEnabled, userId });
  }, []);

  // Notifica al parent window el resultado KYC sin cerrar el widget.
  // El widget permanece abierto para que el usuario lea el resultado en pantalla.
  const handleComplete = (result: KYCResult) => {
    window.parent.postMessage({ type: 'KYC_COMPLETE', result }, '*');
  };

  // El usuario cerró explícitamente la pantalla de resultado dentro del widget.
  // El parent puede entonces cerrar el modal y actualizar su UI.
  const handleDismiss = () => {
    window.parent.postMessage({ type: 'KYC_DISMISS' }, '*');
  };

  if (paramsError) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#fff',
          px: 3,
        }}
      >
        <Typography variant="body1" color="error" align="center">
          Sesión no válida. Por favor, inicia el proceso desde Amarello.com
        </Typography>
      </Box>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <KYCFlowController session={session} onComplete={handleComplete} onDismiss={handleDismiss} />
    </Box>
  );
}
